import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import {InsertDriveFileOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DescriptionText from '../../../shared/descriptiontext/DescriptionText.js';
import ButtonArea from '../../../shared/buttonarea/ButtonArea.js';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import {lerErroDeArquivo, salvarBlob} from '../../components/downloadArquivo.js';
import {
    MENSAGEM_ANO_SEMESTRE_OBRIGATORIO,
    montarParametrosTermosConcessao,
    nomeArquivoTermosConcessao
} from '../../components/relatorio/relatorios.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useFetch from '../../../../hooks/useFetch.js';
import {
    URL_RELATORIO_TERMOS_CONCESSAO,
    URL_RELATORIO_TERMOS_CONCESSAO_PERIODOS
} from '../../../../constants/index.js';
import {containedButton, containerPaper} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const VALORES_INICIAIS = {anoSemestre: ''};

const FormSchema = Yup.object().shape({
    anoSemestre: Yup.string().required(MENSAGEM_ANO_SEMESTRE_OBRIGATORIO)
});

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    ...containedButton(theme, false, false)
}));

/*
 * Relatório de Termos de Concessão de Bolsas (módulo RELCONCBOL): o usuário escolhe o
 * ano/semestre e o back devolve o Excel (.xls), que é baixado (sem visualizador).
 */
export default function PageRelatorioTermosConcessao() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('RELCONCBOL');
    const [fetchPeriodos, carregandoPeriodos] = useFetch({returnInnerLoadingState: true});
    const [baixar, gerando] = useFetch({returnInnerLoadingState: true, responseType: 'blob'});
    const [periodos, setPeriodos] = useState([]);

    const {handleSubmit, control, formState: {errors}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_INICIAIS
    });

    useEffect(() => {
        if (podeAcessar) {
            // Períodos com termos, do mais recente para o mais antigo.
            fetchPeriodos(URL_RELATORIO_TERMOS_CONCESSAO_PERIODOS, PARAMS_VAZIOS,
                dados => setPeriodos(Array.isArray(dados) ? dados : []));
        }
    }, [podeAcessar]);

    const submit = valores => baixar(URL_RELATORIO_TERMOS_CONCESSAO, montarParametrosTermosConcessao(valores),
        blob => salvarBlob(blob, nomeArquivoTermosConcessao(valores.anoSemestre)),
        (erro, tratarErro) => lerErroDeArquivo(erro).then(tratarErro));

    // Loading da página pelo LoadingModal global.
    useCarregamentoPagina(carregandoPeriodos || gerando);

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    const ocupado = carregandoPeriodos || gerando;

    return (
        <PageWrapper>
            <PageTitle title='Relatório de Termos de Concessão de Bolsas'/>
            <DescriptionText>
                Gera o Excel (.xls) dos termos de concessão de bolsas do ano/semestre escolhido.
            </DescriptionText>
            <Paper className={classes.containerPaper}>
                <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <LabelValue label='Ano/Semestre *'>
                                <FormControl variant='outlined' fullWidth error={!!errors.anoSemestre}>
                                    <Controller
                                        name='anoSemestre'
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} displayEmpty error={!!errors.anoSemestre}>
                                                <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                {periodos.map(periodo => (
                                                    <MenuItem key={periodo.codigo} value={String(periodo.codigo)}>
                                                        {periodo.descricao}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}/>
                                    {!!errors.anoSemestre && (
                                        <FormHelperText error>{errors.anoSemestre.message}</FormHelperText>
                                    )}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonArea
                                rightArea={
                                    <Button
                                        type='submit'
                                        variant='contained'
                                        startIcon={<InsertDriveFileOutlined/>}
                                        disabled={ocupado}
                                        className={classes.containedButton}>
                                        Emitir Relatório
                                    </Button>
                                }/>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </PageWrapper>
    );
}
